import { Button, FormControl, FormControlLabel, FormLabel, Grid, Paper, Radio, RadioGroup, Switch } from "@material-ui/core";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import CustomInput from "components/CustomInput";
import DropdownFilter from "components/DropdownFilter";
import NotificationProvider from "components/NotificationProvider";
import { useGetAllActivityItemsQuery, useGetAllActivityTypesQuery } from "features/activity/activitySlice";
import { selectTenantName } from "features/user/userSlice";
import { useGetVehicleTypesQuery } from "features/vehicles/vehicleSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import usePrompt from "hooks/usePrompt";
import { IconDetailing, IconFuel, IconInspect, IconMechanic, IconOil, IconTire } from "icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ruleFieldType, ruleFieldTypeConfig } from "../../consts";
import { useAddMechanicalRuleMutation, useDeleteMechanicalsConfigMutation, useGetMechanicalRuleTypesQuery, useUpdateMechanicalRuleMutation } from "../../mechanicalsSlice";
import { lotMechanicalConfigStyles } from "./style";
import { Alert } from "@mui/material";

const Rule = ({
    rule,
    onCancel,
    selectLot,
    clientLots,
    presetActivityId = null,
    showLotInfo
}) => {
    const classes = lotMechanicalConfigStyles();

    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: vehicleTypes, error: vehicleTypesError, isLoading: isLoadingVehicleTypes } = useGetVehicleTypesQuery();
    let { data: activityTypes, error: activityTypesError, isFetching: isLoadingActivityTypes } = useGetAllActivityTypesQuery();//TODO only mechanicals
    let { data: mechanicalRuleTypes, error: mechanicalRuleTypesError, isFetching: isLoadingMechanicalRuleTypes } = useGetMechanicalRuleTypesQuery();//TODO only mechanicals
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery();
    let [updateMechanicalRule, { isLoading: isSaving }] = useUpdateMechanicalRuleMutation();
    let [deleteRule, { isLoading: isDeleting }] = useDeleteMechanicalsConfigMutation();
    let [addRule, { isLoading: isAdding }] = useAddMechanicalRuleMutation();
    clientLots = clientLots?.map(c => ({ id: c.lotId, name: c.name }));;

    const tenantName = useSelector(selectTenantName);
    const { triggerPrompt } = usePrompt();
    const [showLotDropdown, setShowLotDropdown] = useState(false);

    const ruleOperands = [
        {
            id: 1,
            name: 'And',
        },
        {
            id: 2,
            name: 'Or',
        }
    ]

    const ruleIcons = {
        'oil': <IconOil />,
        'fuel': <IconFuel />,
        'tire': <IconTire />,
        'inspection': <IconInspect />,
        'detailing': <IconDetailing />,
        DEFAULT: <IconMechanic />
    }

    const timeFrameTypeValues = [
        { id: 1, name: 'Absolute' },
        { id: 2, name: 'Relative to last completion' }
    ]

    const getIcon = (activityId) => {
        const parsedName = activityItems?.find(a => a.id == activityId)?.name.split(' ')[0].toLowerCase();
        const icon = ruleIcons[parsedName];
        return icon ?? ruleIcons.DEFAULT
    }

    const [ruleToEdit, setRuleToEdit] = useState({ ...rule });

    useEffect(() => {
        setRuleToEdit(rule);
    }, [rule]);

    const onFieldChange = (field, val) => {
        setRuleToEdit({ ...ruleToEdit, [field]: val })
    }

    const onRuleParamChange = (val, type, subType) => {
        let params = [...(ruleToEdit?.ruleParams ?? [])]
        let index = params.findIndex(p => p.paramType === type && p?.paramSubType === subType)
        if (index === -1) {
            params = [...params, { ['paramType']: type, ['paramSubType']: subType, ['paramValue']: val, ['ruleId']: ruleToEdit?.id }]
        }
        else {
            params[index] = { ...params[index], ['paramValue']: val };
        }
        setRuleToEdit({ ...ruleToEdit, ['ruleParams']: params })
    }

    const onChangeVehicleTypes = (val) => {
        if (val[val.length - 1] == null && val.length !== 0) {
            val = [null];
            onFieldChange("vehicleTypeIdsList", val);
        }
        else {
            if (ruleToEdit.vehicleTypeIdsList?.includes(null)) {
                val = val.filter(v => v != null);
                onFieldChange("vehicleTypeIdsList", val);
            } else {
                onFieldChange("vehicleTypeIdsList", val);
            }
        }
    };

    const onSelectLots = (val) => {
        onFieldChange("lotId", val)
    }

    const onSave = async () => {
        let res;
        if (ruleToEdit?.isNew) {
            res = await addRule({
                clientId: ruleToEdit.clientId,
                lotIds: ruleToEdit?.appliesTo == "specific-lots" || !ruleToEdit?.appliesTo ? (!Array.isArray(ruleToEdit.lotId) ? [ruleToEdit.lotId] : ruleToEdit.lotId) : null,
                activityId: presetActivityId ?? ruleToEdit.activityId,
                vehicleTypeIdsList: ruleToEdit?.vehicleTypeIdsList?.includes(null) ? null : ruleToEdit?.vehicleTypeIdsList,
                calcOnCheckin: ruleToEdit?.calcOnCheckin || false,
                ruleOperand: ruleToEdit?.ruleOperand || 1,
                daysToComplete: ruleToEdit?.daysToComplete,
                preferredVendorId: ruleToEdit?.preferredVendorId,
                ruleParams: ruleToEdit?.ruleParams?.filter(r => r?.paramValue) || [],
                allLots: ruleToEdit?.appliesTo == "all-lots" ? true : false,
                allTenantLots: ruleToEdit?.appliesTo == "all-tenant-lots" ? true : false,
                timeFrameType: ruleToEdit?.timeFrameType
            })
        }
        else {
            var vehicleTypeIdsList = ruleToEdit?.vehicleTypeIdsList?.includes(null) ? null : ruleToEdit?.vehicleTypeIdsList;
            setRuleToEdit({ ...ruleToEdit, ['vehicleTypeIdsList']: vehicleTypeIdsList })
            res = await updateMechanicalRule(ruleToEdit);
        }
        if (!res?.error) {
            NotificationProvider.success('Successfully saved rule');
            onCancel();
        } else {
            NotificationProvider.error('Failed to save rule');
            setRuleToEdit({ ...rule });
        }
    }

    const onDelete = async () => {
        triggerPrompt({
            title: "Delete Rule",
            content: "Are you sure you want to delete the rule?",
            onConfirm: async () => {
                const res = await deleteRule({
                    id: ruleToEdit?.id
                })

                if (!res?.error) {
                    NotificationProvider.success('Successfully deleted rule');
                } else {
                    NotificationProvider.error('Failed to delete rule');
                }
            },
        });
    }

    const cancel = () => {
        if (ruleToEdit?.isNew) {
            onCancel();
        }
        else {
            setRuleToEdit(rule)
        }
    }

    const allRuleConfigs = mechanicalRuleTypes?.filter(c => c.paramType != ruleFieldType.VEHICLE_TYPE);
    const vehicleTypeConfig = mechanicalRuleTypes?.filter(c => c.paramType === ruleFieldType.VEHICLE_TYPE);

    const allRuleConfigsLookup = allRuleConfigs?.reduce(function (map, obj) {
        if (map[obj.activityId]) {
            map[obj.activityId] = [...map[obj.activityId], obj]
        } else {
            map[obj.activityId] = [obj];
        }
        return map;
    }, {})

    const vehicleTypeConfigLookup = vehicleTypeConfig?.reduce(function (map, obj) {
        map[obj.activityId] = obj;
        return map;
    }, {})

    const onActivityChange = (val) => {
        setRuleToEdit({
            ...ruleToEdit,
            activityId: val ? parseInt(val) : val,
            vehicleTypeIdsList: null,
            calcOnCheckin: false,
            ruleOperand: 1,
            daysToComplete: null,
            preferredVendorId: null,
            ruleParams: allRuleConfigsLookup?.[parseInt(val)] || []
        })
    }

    const handleRadioChange = (event) => {
        onFieldChange("appliesTo", event.target.value)
        if (event.target.value == "specific-lots") { setShowLotDropdown(true) }
        else { setShowLotDropdown(false) }
    };

    const filteredActivityItems = activityItems?.filter(a => mechanicalRuleTypes?.find(m => m.activityId === a.id))

    const showTimeFrameType = allRuleConfigsLookup?.[ruleToEdit?.activityId]?.find(c => c.paramType == ruleFieldType.WEEKS || c.paramType == ruleFieldType.MONTHS) ? true : false;

    return (
        <Paper className={`${classes.listItemWrapper} ${ruleToEdit?.isNew ? classes.listItemWrapperNew : ""}`}>
            <div className={classes.iconsWrapper}>
                <div className={classes.iconWrapper}>{getIcon(ruleToEdit.activityId)}</div>
            </div>
            <Grid className={classes.ruleWrapper} container spacing={2}>
                <Grid xs={12} item className={classes.titleWrapper} container>
                    {!ruleToEdit?.isNew ?
                        <Grid item container className={classes.title} spacing={1}>
                            <Grid item>{activityItems?.find(a => a.id == ruleToEdit.activityId)?.name}</Grid>
                            {showLotInfo && ruleToEdit.lotId && <Grid item >at: {clientLots?.find(c => c.id == ruleToEdit.lotId)?.name}</Grid>}
                        </Grid>
                        :
                        <Grid item xs={11}>
                            {presetActivityId > 0 ? <></> :
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    <CustomInput
                                        label="Activity"
                                        elementType="dropdown"
                                        value={ruleToEdit?.activityId}
                                        values={filteredActivityItems}
                                        onChange={val => onActivityChange(val)}
                                        showEmpty
                                    />
                                </Authorize>}
                        </Grid>
                    }
                </Grid>

                {ruleToEdit?.isNew && selectLot &&
                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                        <Grid xs={12} item >
                            <div className={classes.switchWrapper}>
                                <FormControl>
                                    <RadioGroup
                                        value={ruleToEdit?.appliesTo}
                                        onChange={handleRadioChange}
                                        aria-labelledby="lot-radio-group"
                                        name="lot-radio-group"
                                        row
                                    >
                                        <FormControlLabel value="specific-lots" control={<Radio />} label="Specific Lots" />
                                        <FormControlLabel value="all-lots" control={<Radio />} label="All Lots" />
                                        <FormControlLabel value="all-tenant-lots" control={<Radio />} label={`All ${tenantName} Lots`} />
                                    </RadioGroup>
                                </FormControl>
                                {(showLotDropdown || ruleToEdit?.appliesTo == "specific-lots") &&
                                    <div className={classes.lotMultiSelect} >
                                        <DropdownFilter
                                            title={'Select lots'}
                                            identifier={"id"}
                                            values={clientLots}
                                            onSelect={(val) => onSelectLots(val)}
                                            value={!!ruleToEdit?.lotId?.length ? [...ruleToEdit?.lotId] : []}
                                            multiple
                                        />
                                    </div>}
                            </div>
                        </Grid>
                    </Authorize>}

                {(ruleToEdit?.lotId == null && !ruleToEdit?.isNew) ?
                    <Grid item xs={12} direction='row' style={{ display: 'flex', alignItems: 'center' }}>
                        <Alert severity="info">
                            {ruleToEdit?.allTenantLots == true ? <div > This rule applies to all {tenantName} lots</div>
                                : ruleToEdit?.allLots == true ? <div>This rule applies to all lots</div> : null}
                        </Alert></Grid> : null}
                {ruleToEdit?.activityId && <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                    <Grid item xs={12}   >
                        <Grid container className={classes.switchWrapper} >
                            <Grid item >
                                <label >
                                    Consider Check In
                                    <Switch
                                        color="primary"
                                        checked={ruleToEdit?.calcOnCheckin}
                                        onChange={(e) => onFieldChange('calcOnCheckin', e.target.checked)}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                </label>
                            </Grid>
                            {(ruleToEdit?.calcOnCheckin === true) &&
                                <Grid item   >
                                    <label >
                                        Consider on First Check In
                                        <Switch
                                            color="primary"
                                            checked={ruleToEdit?.calcOnFirstCheckin}
                                            onChange={(e) => onFieldChange('calcOnFirstCheckin', e.target.checked)}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                        />
                                    </label>

                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Authorize>
                }
                {ruleToEdit?.activityId && <>

                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                        <Grid item xs={4}>
                            <label >
                                Preferred Vendor
                                <CustomInput
                                    id="preferredVendorId"
                                    //label={"Preferred Vendor"}
                                    value={ruleToEdit?.preferredVendorId}
                                    onChange={(val) => onFieldChange('preferredVendorId', parseInt(val))}
                                    elementType="dropdown"
                                    values={vendors}
                                    showEmpty={true}
                                />
                            </label>
                        </Grid>
                        <Grid item xs={4}>
                            <label>
                                Days to Complete By
                                <CustomInput
                                    //label={"Days to Complete By"}
                                    elementType="input"
                                    value={ruleToEdit?.daysToComplete}
                                    onChange={(e) => onFieldChange('daysToComplete', e)}
                                />
                            </label>
                        </Grid>
                    </Authorize>
                </>}

                {ruleToEdit?.activityId && vehicleTypeConfigLookup?.[ruleToEdit?.activityId] &&
                    <Grid item xs={4}>
                        {!!vehicleTypes?.length &&

                            <label>
                                Vehicle Type
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    <DropdownFilter
                                        //title="Vehicle Type"
                                        identifier={"id"}
                                        values={[{ name: 'All vehicle types', id: null }, ...vehicleTypes]}
                                        showEmpty
                                        value={ruleToEdit?.vehicleTypeIdsList ?? (ruleToEdit?.isNew ? [] : [null])}
                                        onSelect={val => onChangeVehicleTypes(val)}
                                        multiple
                                    />
                                </Authorize>
                            </label>
                        }
                    </Grid>
                }
                {showTimeFrameType &&
                    <Grid item xs={4} className={classes.ruleOperand}>
                        <label>
                            Time Frame Type
                            <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                <CustomInput
                                    //label={'Time Frame Type'}
                                    elementType="dropdown"
                                    value={ruleToEdit?.timeFrameType}
                                    values={timeFrameTypeValues}
                                    onChange={val => onFieldChange('timeFrameType', +val)}
                                    showEmpty
                                    shrink
                                />
                            </Authorize>
                        </label>
                    </Grid>
                }




                <Grid item xs={12} container spacing={1} className={classes.params}>
                    {allRuleConfigsLookup?.[ruleToEdit?.activityId]?.map((param, index) =>
                        <>
                            <Grid item key={index} >
                                <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                    {ruleFieldTypeConfig[param.paramType]?.component(
                                        ruleToEdit?.ruleParams?.find(p => p?.paramType === param?.paramType && p?.paramSubType === param?.paramSubType),
                                        (val) => onRuleParamChange(val, param?.paramType, param?.paramSubType),
                                        param?.paramSubType
                                    )}
                                </Authorize>
                            </Grid>
                            {index !== (allRuleConfigsLookup?.[ruleToEdit?.activityId]?.length - 1) &&
                                <Grid item key={index} className={classes.ruleOperand}>
                                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.VIEW_CONFIG}>
                                        <CustomInput
                                            // label={}
                                            elementType="dropdown"
                                            value={ruleToEdit?.ruleOperand}
                                            values={ruleOperands}
                                            onChange={val => onFieldChange('ruleOperand', parseInt(val))}
                                        />
                                    </Authorize>
                                </Grid>
                            }
                        </>
                    )}
                </Grid>

                <Grid item xs={12} className={classes.btnContainer} style={{ flex: 1, alignContent: 'end' }}>
                    <Authorize profile={permissionProfiles.MECHANICALS_CONFIG.SAVE_CONFIG}>
                        <Button
                            onClick={cancel}
                            className={classes.button}
                            color="primary"
                        >
                            Cancel
                        </Button>
                        {!ruleToEdit?.isNew && <Button
                            onClick={onDelete}
                            className={classes.button}
                            variant="contained"
                            color="primary"
                            disabled={isDeleting}
                        >
                            {(isDeleting) ? "Deleting..." : "Delete"}
                        </Button>}
                        <Button
                            onClick={onSave}
                            className={classes.button}
                            variant="contained"
                            color="primary"
                            disabled={isAdding || isSaving || (!ruleToEdit?.activityId || (selectLot && ruleToEdit?.appliesTo === "specific-lots" && !(!!ruleToEdit?.lotId?.length)))}
                        >
                            {(isAdding || isSaving) ? "Saving..." : "Save"}
                        </Button>
                    </Authorize>
                </Grid>
            </Grid>
        </Paper>
    )
}

export default Rule;